import { useCallback } from 'react';

import {
  invalidateAllQuoteItemMatchesQueries,
  invalidateExtractedTablesQuery,
  useDismissExtractedTable,
} from '@frontend/api';
import { AssistanceRejectedIcon, Button } from '@frontend/legacy-components';

type MarkTableInaccurateActionProps = {
  extractedTableId: number;
  partFileUuid: string;
  /**
   * Called once the table has been dismissed. The dismissed table drops out of the
   * extracted tables list, so the modal showing it has nothing left to render.
   */
  onDismissed: () => void;
};

/**
 * Marks a BEAMS extracted table as inaccurate, hiding it from found-in-files, the
 * PDF overlay, and BOM builder suggestions.
 *
 * NOTE: unlike the runes equivalent this mutates directly rather than dispatching a
 * viewer command, because the extracted table modal also renders outside the viewer.
 */
export const MarkTableInaccurateAction = ({
  extractedTableId,
  partFileUuid,
  onDismissed,
}: MarkTableInaccurateActionProps) => {
  const { mutate: dismissExtractedTable, isLoading } = useDismissExtractedTable(
    {
      queryOptions: {
        onSuccess: () => {
          invalidateExtractedTablesQuery({ partFileUuid });
          // the dismissed table may have been the one driving the "BOM table found
          // in <file>" alert on a quote item, which is a separate query
          invalidateAllQuoteItemMatchesQueries();
          onDismissed();
        },
      },
    },
  );

  const handleClick = useCallback(() => {
    dismissExtractedTable({ extractedTableId });
  }, [dismissExtractedTable, extractedTableId]);

  return (
    <Button
      colorScheme="destructive"
      variant="link"
      size="md"
      icon={<AssistanceRejectedIcon />}
      isDisabled={isLoading}
      onClick={handleClick}
    >
      Mark as inaccurate
    </Button>
  );
};
